import React, { useState, useEffect, useMemo } from "react";
import { CircularProgress, Box } from "@mui/material";
import Swal from "sweetalert2";

import {
  generateAndExecuteMutation,
  graphqlQuery,
} from "../../../data/Axios/DynamicService";

import { HRService, caseManagementUrl } from "../../../config";

import DynamicForm from "../../../data/Axios/DynamicForm";
import { claimApplicationsForm } from "../../../data/DynamicTable/FormsFields/LeaveApplicationForms";
import { allCasesView } from "../../../data/Axios/queries";
import { AllEmployeesQuery } from "../../../data/Queries/HRmanagement/leavemanagement";
import { uploadDocuments } from "../../../data/DocumentManager/documentManagerClient";

/* ---------------------------------------------------------------------
   REST FILE UPLOAD FUNCTION (WITH AUTH HEADER)
------------------------------------------------------------------------ */
const uploadSupportingDocuments = async ({
  claimId,
  employeeId,
  files,
}) => {
  console.log("Uploading files to document manager...", {
    claimId,
    employeeId,
    files,
  });

  return uploadDocuments(
    "/upload/staff-claim",
    {
      claim_id: claimId,
      employee_id: employeeId,
    },
    files
  );
};

/* ---------------------------------------------------------------------
   STAFF CLAIM APPLICATION FORM
------------------------------------------------------------------------ */
const ClaimApplicationForm = ({ data, isEditing, onClose, onAction }) => {
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken") || "{}");
  const loggedInEmployeeId = parseInt(decodedToken.EmployeeId);
  const rolegroupId = parseInt(decodedToken.RoleGroupId);

  const [employees, setEmployees] = useState([]);
  const [cases, setCases] = useState([]);
  const [showCases, setShowCases] = useState(Boolean(data?.caseRelated));
  const [showEmployeeDropDown, setShowEmployeeDropDown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [supportingDocuments, setSupportingDocuments] = useState([]);

  /* -----------------------------------------------------------------
     LOAD STATIC DATA
  ----------------------------------------------------------------- */
  useEffect(() => {
    const loadData = async () => {
      try {
        // Fetch cases and employees independently so one failure doesn't block the other
        const [casesResult, employeeResult] = await Promise.allSettled([
          graphqlQuery(allCasesView, caseManagementUrl.uri),
          graphqlQuery(AllEmployeesQuery, HRService.uri),
        ]);

        if (casesResult.status === "fulfilled") {
          setCases(casesResult.value || []);
        } else {
          console.error("Error loading cases from case management service:", casesResult.reason);
          setCases([]);
        }

        if (employeeResult.status === "fulfilled") {
          setEmployees(employeeResult.value || []);
        } else {
          console.error("Error loading employees:", employeeResult.reason);
          setEmployees([]);
        }

        if (rolegroupId === 1 || rolegroupId === 2) {
          setShowEmployeeDropDown(true);
        }
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  /* -----------------------------------------------------------------
     OPTIONS
  ----------------------------------------------------------------- */
  const employeeOptions = useMemo(
    () =>
      employees.map((e) => ({
        value: e.id,
        label: `${e.firstName} ${e.lastName}`,
      })),
    [employees]
  );

  const caseOptions = useMemo(
    () => cases.map((c) => ({ value: c.id, label: c.title })),
    [cases]
  );

  /* -----------------------------------------------------------------
     FORM FIELDS
  ----------------------------------------------------------------- */
  const fields = useMemo(() => {
    const base = [
      showEmployeeDropDown && {
        name: "applicationMode",
        label: "Apply For",
        type: "select",
        options: [
          { value: "self", label: "Apply for Self" },
          { value: "employee", label: "Apply for Employee" },
        ],
        required: true,
        gridColumn: "span 2",
      },

      showEmployeeDropDown && {
        name: "employeeId",
        label: "Employee",
        type: "select",
        options: employeeOptions,
        required: true,
        gridColumn: "span 2",
        visibleCondition: (values) => values.applicationMode === "employee",
      },

      ...claimApplicationsForm,

      {
        name: "totalAmount",
        label: "Total Amount",
        type: "number",
        gridColumn: "span 2",
      },

      {
        name: "supportingDocuments",
        label: "Upload the supporting documents",
        type: "file",
        inputProps: {
          multiple: true,
          accept: ".pdf,.doc,.docx,.jpg,.jpeg,.png",
          onChange: (e) => {
            const files = Array.from(e.target.files);
            const maxSize = 5 * 1024 * 1024; // 5 MB
            const oversized = files.filter((f) => f.size > maxSize);

            if (oversized.length > 0) {
              Swal.fire({
                icon: "error",
                title: "File too large",
                text: `Each document must be 5MB or smaller. ${oversized.length} file(s) exceeded the limit.`,
              });
              setSupportingDocuments([]);
              e.target.value = ""; // Reset input
            } else {
              console.log("Files Uploaded:", files);
              setSupportingDocuments(files);
            }
          },
        },
        gridColumn: "span 2",
      },
    ].filter(Boolean);

    if (showCases) {
      base.push({
        name: "caseId",
        label: "Case",
        type: "select",
        options: caseOptions,
        gridColumn: "span 2",
      });
    }

    return base;
  }, [showEmployeeDropDown, employeeOptions, caseOptions, showCases]);

  /* -----------------------------------------------------------------
     INITIAL VALUES
  ----------------------------------------------------------------- */
  const initialValues = {
    applicationMode:
      isEditing && data?.employeeId !== loggedInEmployeeId ? "employee" : "self",
    employeeId:
      isEditing && data?.employeeId !== loggedInEmployeeId ? data?.employeeId : "",
    description: data?.description || "",
    caseRelated: data?.caseRelated || false,
    caseId: data?.caseId || "",
    totalAmount: data?.totalAmount || "",
    supportingDocuments: [],
  };

  /* -----------------------------------------------------------------
     STEPS
  ----------------------------------------------------------------- */
  const steps = useMemo(() => {
    const stepFields = [
      "applicationMode",
      "employeeId",
      "description",
      "caseRelated",
      "totalAmount",
      "supportingDocuments",
    ];
    if (showCases) stepFields.push("caseId");

    return [
      {
        stepLabel: "Claim Information",
        stepDescription: "Enter claim details and upload supporting documents",
        icon: "payment",
        fields: stepFields,
      },
    ];
  }, [showCases]);

  /* -----------------------------------------------------------------
     HANDLE SUBMIT
  ----------------------------------------------------------------- */
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const isApplyingForEmployee =
        showEmployeeDropDown && values.applicationMode === "employee";

      const targetEmployeeId = isApplyingForEmployee
        ? values.employeeId
        : loggedInEmployeeId;

      const payload = {
        employeeId: targetEmployeeId,
        description: values.description,
        caseRelated: Boolean(values.caseRelated),
        caseId: values.caseRelated ? values.caseId || null : null,
        ...(values.totalAmount !== "" && {
          totalAmount: parseFloat(values.totalAmount),
        }),
      };

      if (isEditing) payload.id = data.id;

      console.log("Submitting GraphQL payload:", payload);

      const response = await generateAndExecuteMutation(
        isEditing
          ? "updateStaffClaimApplication"
          : "createStaffClaimApplication",
        payload,
        "staffClaim",
        HRService.uri
      );

      const claim = Object.values(response)[0];
      if (!claim?.id) throw new Error("Claim creation failed");
      console.log("Claim created/updated:", claim);

      if (supportingDocuments.length > 0) {
        try {
          await uploadSupportingDocuments({
            claimId: claim.id,
            employeeId: targetEmployeeId,
            files: supportingDocuments,
          });
        } catch (uploadError) {
          console.warn("Document upload failed (non-fatal):", uploadError.message);
          Swal.fire({
            icon: "warning",
            title: "Claim saved — documents not uploaded",
            text: "Your claim was saved successfully, but the supporting documents could not be uploaded. Please try uploading them separately.",
            timer: 5000,
            timerProgressBar: true,
          });
        }
      }

      Swal.fire("Success", "Claim submitted successfully", "success");
      onClose?.();
      onAction?.();
    } catch (error) {
      console.error("Error submitting claim:", error);
      Swal.fire("Error", error.message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  /* -----------------------------------------------------------------
     FIELD CHANGE WATCHER
  ----------------------------------------------------------------- */
  const handleFieldChange = (field, value) => {
    if (field.name === "caseRelated") setShowCases(Boolean(value));
  };

  /* -----------------------------------------------------------------
     RENDER
  ----------------------------------------------------------------- */
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <DynamicForm
      title={isEditing ? "Update Staff Claim" : "Create Staff Claim"}
      subtitle="Fill in the claim details and submit"
      fields={fields}
      steps={steps}
      initialValues={initialValues}
      onFieldChange={handleFieldChange}
      onSubmit={handleSubmit}
    />
  );
};

export default ClaimApplicationForm;
